/**
 * Thin wrapper around sonner so the rest of the app imports a single,
 * consistent helper (and we can swap implementations later).
 */
import { toast } from "sonner";
import i18n from "../i18n.ts";
import { HttpError } from "./api.ts";

/**
 * Localized last-resort error text. The server's `message` (Arabic for all
 * user-visible errors) always wins when present — this only covers network
 * failures and empty messages.
 */
function genericError(): string {
	return i18n.t("common:states.error", { defaultValue: "حدث خطأ" });
}

export function notifySuccess(message: string) {
	toast.success(message);
}

export function notifyInfo(message: string) {
	toast(message);
}

/** Amber, non-fatal heads-up (e.g. a quantity that was auto-clamped). */
export function notifyWarning(message: string) {
	toast.warning(message);
}

// Errors linger longer than the global 3.5s default so a busy user — or a
// screen-reader user — has time to read/hear them before they auto-dismiss.
const ERROR_DURATION_MS = 6000;

export function notifyError(error: unknown, fallback?: string) {
	const opts = { duration: ERROR_DURATION_MS };
	// Several call sites pass an already-localized message string directly.
	// Without this branch a string fell through to the generic fallback and
	// the user saw "Something went wrong" instead of the real (Arabic) text.
	if (typeof error === "string" && error.trim() !== "") {
		toast.error(error, opts);
		return;
	}
	if (error instanceof HttpError) {
		const code = error.error.code;
		const msg = error.error.message ?? "";
		// The server crafts Arabic messages for user-facing business errors, but
		// Zod validation (400) and unhandled 5xx carry raw ENGLISH ("Invalid
		// request data" / "Internal server error"). Map those known codes — and,
		// as a catch-all, any message with no Arabic at all (a technical string) —
		// to locale-aware text so an Arabic (or Kurdish/English) UI never shows a
		// raw server string. Business errors (Arabic message) pass through as-is.
		if (code === "VALIDATION_FAILED") {
			toast.error(
				i18n.t("common:errors.validation", {
					defaultValue: "تحقّق من المعلومات المُدخلة وحاول مرة ثانية.",
				}),
				opts,
			);
			return;
		}
		if (code === "INTERNAL_ERROR" || error.status >= 500) {
			toast.error(
				i18n.t("common:errors.server", {
					defaultValue: "صار خطأ بالخادم. حاول بعد شوية أو تواصل مع الدعم.",
				}),
				opts,
			);
			return;
		}
		if (msg && !/[؀-ۿ]/.test(msg)) {
			// Some other status with a non-Arabic (raw/technical) message.
			toast.error(fallback ?? genericError(), opts);
			return;
		}
		// Precise, Arabic, user-facing text.
		toast.error(msg || fallback || genericError(), opts);
		return;
	}
	if (error instanceof Error) {
		// A thrown Error's message is usually a technical/network string
		// ("Failed to fetch", "NetworkError…", "Load failed") — never show that to
		// a user. Keep an app-thrown (localized) message, but swap the known
		// network/technical ones for the localized generic.
		const m = error.message;
		const technical =
			!m ||
			/failed to fetch|networkerror|network request failed|load failed|timeout|aborted/i.test(
				m,
			);
		toast.error(technical ? (fallback ?? genericError()) : m, opts);
		return;
	}
	toast.error(fallback ?? genericError(), opts);
}

/** Wrap an async mutation in success / error toasts. */
export function withToast<T>(
	promise: Promise<T>,
	options: { success: string; error?: string },
): Promise<T> {
	promise.then(
		() => notifySuccess(options.success),
		(err: unknown) => notifyError(err, options.error),
	);
	return promise;
}
